import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import DemoPlayer from "@/pages/detail/components/DemoPlayer";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { formatViews, getById, getRelated } from "@/lib/catalog";
import { TYPE_LABELS } from "@/types/movie";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getById(id ?? "");
  const [activeEpisode, setActiveEpisode] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState("");

  useDocumentMeta(
    movie ? `${movie.title} - 亚洲视频一区` : "影视详情 - 亚洲视频一区",
    movie
      ? `${movie.title}（${movie.year}）${movie.genres.join("、")}题材影视作品，${movie.region}出品。${movie.summary.slice(0, 60)}…亚洲视频一区在线观看。`
      : "影视作品详情与在线播放演示。",
    movie
      ? `${movie.title},亚洲视频一区,${TYPE_LABELS[movie.type]},${movie.genres.join(",")},在线观看`
      : "亚洲视频一区,在线观看,影视详情"
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-600">内容可能已下线，去首页看看其他精彩影视吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(movie, 5);
  const episodes = movie.episodes ?? [];

  return (
    <div className="w-full pb-4">
      {/* Hero backdrop */}
      <section className="relative w-full">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/60" />
        </div>

        <div className="relative w-full px-4 md:px-8 lg:px-12 pt-8 md:pt-12 pb-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.type}`}
              className="hover:text-primary-500 transition-colors"
            >
              {TYPE_LABELS[movie.type]}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 truncate max-w-[180px]">{movie.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-52 shrink-0 rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
              <div className="relative w-full aspect-[5/7]">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title}`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs md:text-sm">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-500 text-background-950 font-heading font-bold">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-800">
                  {TYPE_LABELS[movie.type]}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-800">
                  {movie.year}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-800">
                  {movie.region}
                </span>
                {movie.genres.map((g) => (
                  <span
                    key={g}
                    className="px-2.5 py-1 rounded-md bg-secondary-500/15 text-secondary-500"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <p className="mt-4 text-sm text-foreground-700 leading-relaxed max-w-3xl">
                {movie.summary}
              </p>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm max-w-2xl">
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">导演</dt>
                  <dd className="text-foreground-800 truncate">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">时长</dt>
                  <dd className="text-foreground-800 truncate">{movie.duration}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-foreground-500 shrink-0">主演</dt>
                  <dd className="text-foreground-800 truncate">{movie.cast.join("、")}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-foreground-500 shrink-0">热度</dt>
                  <dd className="text-foreground-800 flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500" />
                    {formatViews(movie.views)} 次播放 · 更新于 {movie.updatedAt}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFavorited((v) => !v);
                    showToast(favorited ? "已取消收藏（本地演示状态）" : "已加入收藏（本地演示状态）");
                  }}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-900 hover:bg-background-300"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(window.location.href).catch(() => undefined);
                    }
                    showToast("链接已复制（分享为演示功能）");
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-200/70 text-foreground-900 text-sm font-semibold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <Link
                  to={`/category/${movie.type}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-primary-500 border border-primary-500/40 text-sm font-semibold hover:bg-primary-500/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-list-line" />
                  进入{TYPE_LABELS[movie.type]}频道
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Player */}
      <section className="w-full px-4 md:px-8 lg:px-12 mt-2">
        <div className="mb-4 flex items-center gap-2">
          <i className="ri-live-line text-primary-500 text-lg" />
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
            在线播放
          </h2>
          <span className="text-xs text-foreground-500">
            （前端播放演示，暂无真实视频源）
          </span>
        </div>
        <DemoPlayer
          title={movie.title}
          image={movie.backdrop}
          poster={movie.poster}
          subtitle={episodes[activeEpisode] ? `${movie.title} · ${episodes[activeEpisode]}` : movie.title}
        />
      </section>

      {episodes.length > 0 && (
        <section className="w-full px-4 md:px-8 lg:px-12 mt-6">
          <EpisodeList episodes={episodes} active={activeEpisode} onSelect={setActiveEpisode} />
          <p className="mt-3 text-xs text-foreground-500">
            当前选择：{episodes[activeEpisode]}（点击剧集可切换演示播放内容）
          </p>
        </section>
      )}

      {/* Related */}
      <section className="w-full px-4 md:px-8 lg:px-12 mt-12 md:mt-16">
        <SectionHeading
          icon="ri-emotion-happy-line"
          title="相关推荐"
          subtitle="根据类型与标签为你推荐"
          moreTo={`/category/${movie.type}`}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
          {related.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-lg bg-background-200 border border-background-300 text-sm text-foreground-900 animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}